import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Loader2, CheckCircle2, AlertCircle, Plus, CalendarDays } from "lucide-react";
import type { Vessel } from "@/hooks/useVessels";
import { useVarieties } from "@/hooks/useVarieties";
import { useVolumeUnit } from "@/hooks/useOrganization";
import { useAuth } from "@/contexts/AuthContext";
import {
  parseLotCode,
  useCreatePlanningLot,
  type PlanningLotWithClient,
} from "@/hooks/usePlanningLots";
import { useCreateAssignment } from "@/hooks/useVesselAssignments";
import { getVarietyColor } from "@/hooks/useVarietyColorMap";
import { useVarietyColorMap } from "@/hooks/useVarietyColorMap";

interface PlanLotDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When opened from a timeline cell double-click, pre-sets the target vessel and fill date */
  defaultVessel?: Vessel | null;
  defaultDate?: Date | null;
}

// Fetches an existing wine_lot by exact code
function useWineLotByCode(code: string, enabled: boolean) {
  return useQuery({
    queryKey: ["wine-lot-by-code", code],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("wine_lot")
        .select(`
          id, code, varietal, vintage, current_volume_l,
          client!wine_lot_client_id_fkey (id, name)
        `)
        .eq("code", code)
        .maybeSingle();
      if (error) throw error;
      return data as {
        id: string; code: string; varietal: string | null; vintage: number | null;
        current_volume_l: number | null; client: { id: string; name: string } | null;
      } | null;
    },
    enabled,
    staleTime: 30000,
  });
}

// Fetches an existing planning_lot by exact code
function usePlanningLotByCodeLocal(code: string, enabled: boolean) {
  return useQuery({
    queryKey: ["planning-lot-by-code", code],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("planning_lot")
        .select("*, client:client_id(id, name)")
        .eq("code", code)
        .maybeSingle();
      if (error) throw error;
      return data as PlanningLotWithClient | null;
    },
    enabled,
    staleTime: 10000,
  });
}

export function PlanLotDialog({ open, onOpenChange, defaultVessel, defaultDate }: PlanLotDialogProps) {
  const [codeInput, setCodeInput] = useState("");
  const [volume, setVolume] = useState("");
  // When neither lot is found, user can expand a form to create a planning lot
  const [showCreateForm, setShowCreateForm] = useState(false);
  // Which found result the user has selected: "wine_lot" | "planning_lot"
  const [selectedSource, setSelectedSource] = useState<"wine_lot" | "planning_lot" | null>(null);

  const { volumeLabel } = useVolumeUnit();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { data: varieties = [] } = useVarieties(true);
  const colorMap = useVarietyColorMap();
  const createPlanningLot = useCreatePlanningLot();
  const createAssignment = useCreateAssignment();

  // Normalize: uppercase, no spaces
  const normalizedCode = codeInput.toUpperCase().replace(/\s/g, "");
  const canSearch = normalizedCode.length >= 6;
  const parsed = useMemo(() => parseLotCode(normalizedCode), [normalizedCode]);

  // Resolve varietal + color from code's VV segment
  const resolvedVariety = useMemo(() => {
    if (!parsed) return null;
    return varieties.find(v => v.code.toUpperCase() === parsed.vv) ?? null;
  }, [parsed, varieties]);

  const varietyColor = resolvedVariety
    ? getVarietyColor(colorMap, resolvedVariety.name)
    : "#64748b";

  // Resolve client from CC segment (for the "create" form pre-fill)
  const { data: clientsData } = useQuery({
    queryKey: ["clients", "with-codes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client").select("id, name, code")
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []).filter((c: any) => c.is_active !== false);
    },
    enabled: isAuthenticated && !authLoading,
  });
  const resolvedClient = useMemo(() => {
    if (!parsed || !clientsData) return null;
    return clientsData.find((c: any) => (c.code || "").toUpperCase() === parsed.cc) ?? null;
  }, [parsed, clientsData]);

  // Dual lookup
  const { data: wineLot, isLoading: wineLotLoading } = useWineLotByCode(normalizedCode, canSearch);
  const { data: planningLot, isLoading: planningLotLoading } = usePlanningLotByCodeLocal(normalizedCode, canSearch);

  const isSearching = canSearch && (wineLotLoading || planningLotLoading);
  const nothingFound = canSearch && !isSearching && !wineLot && !planningLot;

  // Auto-select the first found result when results arrive
  const autoSelectedSource = useMemo(() => {
    if (wineLot) return "wine_lot" as const;
    if (planningLot) return "planning_lot" as const;
    return null;
  }, [wineLot, planningLot]);

  const effectiveSource = selectedSource ?? autoSelectedSource;

  // Default volume from whichever lot is selected
  const defaultVolume = useMemo(() => {
    if (effectiveSource === "wine_lot") return wineLot?.current_volume_l?.toString() ?? "";
    if (effectiveSource === "planning_lot") return planningLot?.current_volume_l?.toString() ?? "";
    return "";
  }, [effectiveSource, wineLot, planningLot]);

  // Sync volume field when selection changes (only if user hasn't typed one)
  const [volumeTouched, setVolumeTouched] = useState(false);
  const displayVolume = volumeTouched ? volume : defaultVolume;

  const reset = () => {
    setCodeInput("");
    setVolume("");
    setShowCreateForm(false);
    setSelectedSource(null);
    setVolumeTouched(false);
  };

  const handleSubmit = async () => {
    const vol = parseFloat(displayVolume) || undefined;
    const vesselId = defaultVessel?.id ?? null;
    const fillDate = format(defaultDate ?? new Date(), "yyyy-MM-dd");

    if (effectiveSource === "wine_lot" && wineLot) {
      await createAssignment.mutateAsync({
        vessel_id: vesselId,
        wine_lot_id: wineLot.id,
        fill_date: fillDate,
        fill_volume_l: vol ?? null,
      });
      onOpenChange(false);
      reset();
      return;
    }

    if (effectiveSource === "planning_lot" && planningLot) {
      await createAssignment.mutateAsync({
        vessel_id: vesselId,
        planning_lot_id: planningLot.id,
        fill_date: fillDate,
        fill_volume_l: vol ?? null,
      });
      onOpenChange(false);
      reset();
      return;
    }

    // Create new planning lot, then assignment
    if (showCreateForm && parsed) {
      const newLot = await createPlanningLot.mutateAsync({
        code: normalizedCode,
        client_id: resolvedClient?.id ?? null,
        varietal: resolvedVariety?.name ?? null,
        vintage: parsed.vintageYear,
        current_volume_l: vol ?? null,
      });
      await createAssignment.mutateAsync({
        vessel_id: vesselId,
        planning_lot_id: newLot.id,
        fill_date: fillDate,
        fill_volume_l: vol ?? null,
      });
      onOpenChange(false);
      reset();
    }
  };

  const isPending = createPlanningLot.isPending || createAssignment.isPending;

  const canSubmit =
    !isPending &&
    canSearch &&
    (effectiveSource === "wine_lot" || effectiveSource === "planning_lot" || showCreateForm);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Plan a Lot</DialogTitle>
          {defaultVessel && defaultDate && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
              <CalendarDays className="h-3.5 w-3.5 flex-shrink-0" />
              <span>
                Placing on <span className="font-medium text-foreground">{defaultVessel.name}</span> starting{" "}
                <span className="font-medium text-foreground">{format(defaultDate, "MMM d, yyyy")}</span>
              </span>
            </div>
          )}
        </DialogHeader>

        <div className="space-y-4">
          {/* Code input */}
          <div>
            <Label htmlFor="lot-code">Lot Code</Label>
            <Input
              id="lot-code"
              value={codeInput}
              onChange={e => {
                setCodeInput(e.target.value);
                setSelectedSource(null);
                setShowCreateForm(false);
                setVolumeTouched(false);
              }}
              placeholder="Lot code"
              className="font-mono uppercase tracking-wider mt-1"
              autoFocus
              autoComplete="off"
            />
          </div>

          {/* Live code parse preview */}
          {parsed && (
            <div className="rounded-md border bg-muted/30 px-3 py-2 space-y-1">
              <div className="flex items-center gap-2">
                {/* Varietal color swatch */}
                <span
                  className="h-3 w-3 rounded-full flex-shrink-0 border border-white/20"
                  style={{ backgroundColor: varietyColor }}
                />
                <span className="font-mono text-sm font-semibold tracking-wide">
                  <span className="text-blue-500">{parsed.cc}</span>
                  <span className="text-amber-500">{parsed.yy}</span>
                  <span className="text-emerald-500">{parsed.vv}</span>
                  {parsed.identifier && (
                    <span className="text-purple-500">{parsed.identifier}</span>
                  )}
                </span>
              </div>
              <div className="flex gap-3 text-[10px] text-muted-foreground">
                <span className="text-blue-500/80">
                  {resolvedClient ? resolvedClient.name : `Client "${parsed.cc}" unknown`}
                </span>
                <span className="text-amber-500/80">{parsed.vintageYear ?? "?"}</span>
                <span className="text-emerald-500/80">
                  {resolvedVariety ? resolvedVariety.name : `Varietal "${parsed.vv}" unknown`}
                </span>
                {parsed.identifier && (
                  <span className="text-purple-500/80">{parsed.identifier}</span>
                )}
              </div>
            </div>
          )}

          {/* Search results */}
          {canSearch && (
            <div className="space-y-2">
              {isSearching && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Searching…
                </div>
              )}

              {/* Found in production */}
              {wineLot && (
                <button
                  type="button"
                  onClick={() => setSelectedSource("wine_lot")}
                  className={cn(
                    "w-full text-left rounded-md border px-3 py-2 text-sm transition-colors",
                    effectiveSource === "wine_lot"
                      ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30"
                      : "hover:border-muted-foreground/50"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                    <span className="font-medium font-mono">{wineLot.code}</span>
                    <span className="text-xs text-emerald-600 dark:text-emerald-400">In production</span>
                  </div>
                  {(wineLot.client || wineLot.varietal || wineLot.vintage) && (
                    <div className="text-xs text-muted-foreground mt-0.5 ml-5">
                      {[wineLot.client?.name, wineLot.varietal, wineLot.vintage].filter(Boolean).join(" · ")}
                      {wineLot.current_volume_l && ` · ${wineLot.current_volume_l.toLocaleString()} L`}
                    </div>
                  )}
                </button>
              )}

              {/* Found in planner */}
              {planningLot && (
                <button
                  type="button"
                  onClick={() => setSelectedSource("planning_lot")}
                  className={cn(
                    "w-full text-left rounded-md border px-3 py-2 text-sm transition-colors",
                    effectiveSource === "planning_lot"
                      ? "border-amber-500 bg-amber-50 dark:bg-amber-950/30"
                      : "hover:border-muted-foreground/50"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                    <span className="font-medium font-mono">{planningLot.code}</span>
                    <span className="text-xs text-amber-600 dark:text-amber-400">
                      {planningLot.is_received ? "Received (planned)" : "In planner"}
                    </span>
                  </div>
                  {(planningLot.client || planningLot.varietal || planningLot.vintage) && (
                    <div className="text-xs text-muted-foreground mt-0.5 ml-5">
                      {[planningLot.client?.name, planningLot.varietal, planningLot.vintage].filter(Boolean).join(" · ")}
                      {planningLot.current_volume_l && ` · ${planningLot.current_volume_l.toLocaleString()} L`}
                    </div>
                  )}
                </button>
              )}

              {/* Nothing found — offer to create */}
              {nothingFound && !showCreateForm && (
                <div className="rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground">
                  <p>No lot found for <span className="font-mono font-medium">{normalizedCode}</span>.</p>
                  <button
                    type="button"
                    className="mt-1.5 flex items-center gap-1 text-primary hover:underline text-xs"
                    onClick={() => setShowCreateForm(true)}
                  >
                    <Plus className="h-3 w-3" />
                    Create as planning lot
                  </button>
                </div>
              )}

              {/* Create form */}
              {nothingFound && showCreateForm && (
                <div className="rounded-md border bg-muted/20 px-3 py-2 space-y-1 text-sm">
                  <p className="text-xs font-medium text-muted-foreground">New planning lot</p>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    <div>
                      <span className="text-muted-foreground">Client</span>
                      <p className="font-medium truncate">
                        {resolvedClient ? resolvedClient.name : <span className="text-amber-500">{parsed?.cc} (unknown)</span>}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Vintage</span>
                      <p className="font-medium">{parsed?.vintageYear ?? "—"}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Varietal</span>
                      <p className="font-medium flex items-center gap-1">
                        {resolvedVariety ? (
                          <>
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: varietyColor }} />
                            {resolvedVariety.name}
                          </>
                        ) : (
                          <span className="text-amber-500">{parsed?.vv} (unknown)</span>
                        )}
                      </p>
                    </div>
                    {parsed?.identifier && (
                      <div>
                        <span className="text-muted-foreground">Identifier</span>
                        <p className="font-medium">{parsed.identifier}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Volume — shown once a lot is selected or being created */}
          {(effectiveSource || showCreateForm) && (
            <div>
              <Label htmlFor="volume">Volume ({volumeLabel}) <span className="text-muted-foreground font-normal">— optional</span></Label>
              <Input
                id="volume"
                type="number"
                value={displayVolume}
                onChange={e => { setVolume(e.target.value); setVolumeTouched(true); }}
                placeholder="e.g. 15000"
                min="0"
                step="0.01"
                className="mt-1"
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit}>
              {isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Planning…</>
              ) : (
                defaultVessel ? "Place on Tank" : "Add to On Deck"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
